import { Plus, X } from 'lucide-react'
import { useId } from 'react'
import * as m from '~/paraglide/messages'
import { storageManager } from '~/shared/lib/storage-manager'
import { Button } from '~/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'
import { Checkbox } from '~/shared/ui/checkbox'
import { Label } from '~/shared/ui/label'
import { Switch } from '~/shared/ui/switch'

interface HourEntry {
  day: string
  open: string
  close: string
}

interface ListingHoursSectionProps {
  // biome-ignore lint/suspicious/noExplicitAny: TanStack Form API has too many generic params to reasonably type here
  form: any
  bufferKey: string
}

const DAY_KEYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const

const getDayLabel = (day: string) => {
  const labels: Record<string, () => string> = {
    Sunday: m.sunday,
    Monday: m.monday,
    Tuesday: m.tuesday,
    Wednesday: m.wednesday,
    Thursday: m.thursday,
    Friday: m.friday,
    Saturday: m.saturday,
  }
  return labels[day]?.() ?? day
}

export function ListingHoursSection({ form, bufferKey }: ListingHoursSectionProps) {
  const noHoursId = useId()
  const bufferStorageKey = `pulsify-listing-hours-buffer-${bufferKey}`

  const groupHoursByDay = (hours: HourEntry[]) => {
    const grouped: Record<string, HourEntry[]> = {}
    hours?.forEach((hour: HourEntry) => {
      if (!grouped[hour.day]) {
        grouped[hour.day] = []
      }
      grouped[hour.day].push(hour)
    })
    return grouped
  }

  const toggleDay = (day: string, isEnabled: boolean) => {
    const currentHours = form.getFieldValue('hours') || []
    const dayStr = String(day)

    const updateHours = () => {
      if (isEnabled) {
        const newHours = currentHours.filter((h: HourEntry) => h.day !== dayStr)
        form.setFieldValue('hours', newHours)
      } else {
        const lastEntry = currentHours[currentHours.length - 1]
        const defaultOpen = lastEntry?.open || '09:00:00'
        const defaultClose = lastEntry?.close || '17:00:00'

        form.setFieldValue('hours', [
          ...currentHours,
          {
            day: dayStr,
            open: defaultOpen,
            close: defaultClose,
          },
        ])
      }
    }

    if (document.startViewTransition) {
      const transition = document.startViewTransition(() => {
        updateHours()
      })
      transition.ready.then(() => {
        document.documentElement.style.setProperty('view-transition-duration', '150ms')
      })
    } else {
      updateHours()
    }
  }

  const addTimeRange = (day: string) => {
    const currentHours = form.getFieldValue('hours') || []
    const dayStr = String(day)
    const existingForDay = currentHours.filter((h: HourEntry) => h.day === dayStr)

    const lastForDay = existingForDay[existingForDay.length - 1]

    let defaultOpen = '14:00:00'
    let defaultClose = '18:00:00'

    if (lastForDay?.close) {
      const closeTime = lastForDay.close.substring(0, 5)
      const [hours, minutes] = closeTime.split(':').map(Number)
      const nextHour = (hours + 1) % 24
      defaultOpen = `${String(nextHour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`

      const closeHour = (nextHour + 4) % 24
      defaultClose = `${String(closeHour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`
    }

    form.setFieldValue('hours', [
      ...currentHours,
      {
        day: dayStr,
        open: defaultOpen,
        close: defaultClose,
      },
    ])
  }

  const removeTimeRange = (day: string, rangeIndex: number) => {
    const currentHours = form.getFieldValue('hours') || []
    const dayStr = String(day)
    let dayRangeCount = 0
    const newHours = currentHours.filter((h: HourEntry) => {
      if (h.day !== dayStr) return true
      const keep = dayRangeCount !== rangeIndex
      dayRangeCount++
      return keep
    })
    form.setFieldValue('hours', newHours)
  }

  const updateTimeRange = (day: string, rangeIndex: number, open: string, close: string) => {
    const currentHours = form.getFieldValue('hours') || []
    const dayStr = String(day)
    let dayRangeCount = 0
    const newHours = currentHours.map((h: HourEntry) => {
      if (h.day !== dayStr) return h
      const isTarget = dayRangeCount === rangeIndex
      dayRangeCount++
      return isTarget ? { ...h, open, close } : h
    })
    form.setFieldValue('hours', newHours)
  }

  const addWeekdayHours = () => {
    const currentHours = form.getFieldValue('hours') || []
    const usedDays = new Set(currentHours.map((h: HourEntry) => h.day))
    const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']

    const lastEntry = currentHours[currentHours.length - 1]
    const defaultOpen = lastEntry?.open || '09:00:00'
    const defaultClose = lastEntry?.close || '17:00:00'

    const newHours = weekdays
      .filter((day) => !usedDays.has(day))
      .map((day) => ({
        day,
        open: defaultOpen,
        close: defaultClose,
      }))

    if (newHours.length > 0) {
      form.setFieldValue('hours', [...currentHours, ...newHours])
    }
  }

  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle>{m.openingHours()}</CardTitle>
        <CardDescription>{m.setYourBusinessOperatingHours()}</CardDescription>
      </CardHeader>
      <CardContent>
        <form.Subscribe
          selector={(state: { values: { hours?: HourEntry[] } }) =>
            (state.values.hours?.length ?? 0) === 0
          }
        >
          {(noHours: boolean) => (
            <>
              <div className="flex items-center gap-3 pb-4">
                <Switch
                  id={noHoursId}
                  checked={noHours}
                  onCheckedChange={(checked: boolean) => {
                    if (checked) {
                      const current = form.getFieldValue('hours') || []
                      if (current.length > 0) {
                        storageManager.set(bufferStorageKey, current)
                      }
                      form.setFieldValue('hours', [])
                      return
                    }
                    const buffered = storageManager.get<HourEntry[]>(bufferStorageKey)
                    form.setFieldValue(
                      'hours',
                      buffered && buffered.length > 0
                        ? buffered
                        : [
                            {
                              day: 'Monday',
                              open: '09:00:00',
                              close: '17:00:00',
                            },
                          ],
                    )
                  }}
                />
                <Label htmlFor={noHoursId} className="cursor-pointer">
                  {m.noExplicitOpeningHours()}
                </Label>
              </div>

              {noHours ? (
                <p className="text-sm italic text-muted-foreground">{m.noOpeningHoursHint()}</p>
              ) : (
                <form.Field name="hours">
                  {(field: { state: { value: HourEntry[] } }) => {
                    const groupedHours = groupHoursByDay(field.state.value || [])
                    const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
                    const missingWeekdays = weekdays.filter((day) => !groupedHours[day])

                    return (
                      <>
                        {missingWeekdays.length > 0 && (
                          <div className="flex justify-end pb-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={addWeekdayHours}
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              {field.state.value && field.state.value.length > 0
                                ? m.addWeekdays({
                                    count: missingWeekdays.length,
                                  })
                                : m.setWeekdayHours()}
                            </Button>
                          </div>
                        )}
                        <div className="space-y-2">
                          {DAY_KEYS.map((day) => {
                            const dayRanges = groupedHours[day] || []
                            const isEnabled = dayRanges.length > 0

                            return (
                              <div key={day}>
                                <div className="flex flex-wrap items-center gap-3 min-h-10">
                                  <Checkbox
                                    checked={isEnabled}
                                    onCheckedChange={(_checked) => toggleDay(day, isEnabled)}
                                    id={`day-${day}`}
                                  />
                                  <Label
                                    htmlFor={`day-${day}`}
                                    className={`w-24 cursor-pointer transition-colors ${
                                      isEnabled ? '' : 'text-muted-foreground'
                                    }`}
                                  >
                                    {getDayLabel(day)}
                                  </Label>

                                  {isEnabled && dayRanges.length > 0 ? (
                                    <div className="w-full flex flex-wrap gap-2 min-w-0 sm:w-auto sm:flex-1">
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="time"
                                          className="h-8 w-30 rounded-md border border-input bg-transparent px-2 py-1 text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                          value={dayRanges[0]?.open?.substring(0, 5) || '09:00'}
                                          onChange={(e) => {
                                            updateTimeRange(
                                              day,
                                              0,
                                              e.target.value,
                                              dayRanges[0].close,
                                            )
                                          }}
                                        />
                                        <span className="text-muted-foreground text-xs">-</span>
                                        <input
                                          type="time"
                                          className="h-8 w-30 rounded-md border border-input bg-transparent px-2 py-1 text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                          value={dayRanges[0]?.close?.substring(0, 5) || '17:00'}
                                          onChange={(e) => {
                                            updateTimeRange(
                                              day,
                                              0,
                                              dayRanges[0].open,
                                              e.target.value,
                                            )
                                          }}
                                        />
                                      </div>

                                      {dayRanges.length > 1 ? (
                                        <div className="flex flex-wrap items-center gap-2">
                                          <input
                                            type="time"
                                            className="h-8 w-30 rounded-md border border-input bg-transparent px-2 py-1 text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                            value={dayRanges[1]?.open?.substring(0, 5) || '14:00'}
                                            onChange={(e) => {
                                              updateTimeRange(
                                                day,
                                                1,
                                                e.target.value,
                                                dayRanges[1].close,
                                              )
                                            }}
                                          />
                                          <span className="text-muted-foreground text-xs">-</span>
                                          <input
                                            type="time"
                                            className="h-8 w-30 rounded-md border border-input bg-transparent px-2 py-1 text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                            value={dayRanges[1]?.close?.substring(0, 5) || '18:00'}
                                            onChange={(e) => {
                                              updateTimeRange(
                                                day,
                                                1,
                                                dayRanges[1].open,
                                                e.target.value,
                                              )
                                            }}
                                          />
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.preventDefault()
                                              e.stopPropagation()
                                              removeTimeRange(day, 1)
                                            }}
                                            className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-muted transition-colors"
                                          >
                                            <X className="h-3.5 w-3.5" />
                                          </button>
                                        </div>
                                      ) : (
                                        (() => {
                                          let placeholderOpen = '14:00'
                                          let placeholderClose = '18:00'

                                          if (dayRanges[0]?.close) {
                                            const closeTime = dayRanges[0].close.substring(0, 5)
                                            const [hours, minutes] = closeTime
                                              .split(':')
                                              .map(Number)
                                            const nextHour = (hours + 1) % 24
                                            placeholderOpen = `${String(nextHour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`

                                            const closeHour = (nextHour + 4) % 24
                                            placeholderClose = `${String(closeHour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
                                          }

                                          return (
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.preventDefault()
                                                addTimeRange(day)
                                              }}
                                              className="flex flex-wrap items-center gap-2 opacity-40 hover:opacity-100 transition-opacity cursor-pointer group"
                                            >
                                              <input
                                                type="time"
                                                disabled
                                                className="h-8 w-30 rounded-md border border-dashed border-input bg-muted px-2 py-1 text-sm text-muted-foreground cursor-pointer pointer-events-none"
                                                value={placeholderOpen}
                                              />
                                              <span className="text-muted-foreground text-xs pointer-events-none">
                                                -
                                              </span>
                                              <input
                                                type="time"
                                                disabled
                                                className="h-8 w-30 rounded-md border border-dashed border-input bg-muted px-2 py-1 text-sm text-muted-foreground cursor-pointer pointer-events-none"
                                                value={placeholderClose}
                                              />
                                              <div className="h-8 w-8 flex items-center justify-center rounded-md text-muted-foreground group-hover:text-foreground transition-colors pointer-events-none">
                                                <Plus className="h-3.5 w-3.5" />
                                              </div>
                                            </button>
                                          )
                                        })()
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-sm text-muted-foreground italic">
                                      {m.closed()}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </>
                    )
                  }}
                </form.Field>
              )}
            </>
          )}
        </form.Subscribe>
      </CardContent>
    </Card>
  )
}
